<template>
  <tile>
    <p class="title">To-Do List</p>
    <checklist-item-wrapper
      :title="'Income Verification'"
      :complete="false"
      v-show="!incomeStipsExist"
    >
      <Income
        :applicant="applicant"
        :application="application"
        @incomeSourcesUpdated="getApplicationStips"
      />
    </checklist-item-wrapper>
    <template v-if="incomeStipsExist">
      <application-checklist-item
        v-for="(item, index) in sortedItemsState.filter((item: any) => item.complete !== true && item.formattedTitle === 'Income Verification')"
        :key="index"
        :item="item"
        @handleUploadSuccess="handleUploadSuccess($event)"
        @incomeSourcesUpdated="getApplicationStips"
      />
      <p v-if="showCompletedItems" class="title">Completed Items</p>
      <application-checklist-item
        v-for="(item, index) in sortedItemsState.filter((item: any) => item.complete === true && item.formattedTitle === 'Income Verification')"
        :key="index"
        :item="item"
      />
    </template>
  </tile>
</template>

<script setup lang="ts">
import { defineProps, reactive, defineEmits, onMounted, computed } from "vue";
import type { PropType } from "vue";
import { useStore } from "vuex";
import { Application, Applicant } from "@/models/application";
import Tile from "@/components/shared/Tile.vue";
import ApplicationChecklistItem from "@/components/ApplicationChecklistItem.vue";
import useChecklistHelpers from "@/use/checklistHelpers";
import Income from "@/components/checklist/Income.vue";
import ChecklistItemWrapper from "@/components/checklist/ChecklistItemWrapper.vue";
import { StipulationStatusIds } from "@/config/constants";

const { updateStipulationStatus, replaceObjectWithId } = useChecklistHelpers();

const props = defineProps({
  applicant: Object as PropType<Applicant>,
  application: Object as PropType<Application>,
});

const emit = defineEmits(["hydrateStore"]);
const store = useStore();

const showCompletedItems = computed<boolean>(
  () =>
    sortedItemsState.value?.filter(
      (item: any) => item.complete === true && item.formattedTitle === "Income Verification",
    )?.length > 0,
);
const applicationId = computed(() => store.state.appData.application.id);

const selectedIncomeSourcesCount = computed(() => store.getters["SelectedIncomeSourcesCount"]);
const sortedItemsState = computed(() => store.state.checklistModule.sortedItems);
const incomeStipsExist = computed(() => {
  if (sortedItemsState?.value && sortedItemsState?.value?.length > 0) {
    let stips = sortedItemsState?.value?.find((s: any) => s.title === "Income Verification")?.items;
    return stips?.length > 0;
  } else {
    return undefined;
  }
});

let sortedItems = reactive([] as any[]);

async function getApplicationStips() {
  await store.dispatch("checklistModule/getSortedChecklistItems");
}

async function handleUploadSuccess(event: any) {
  const status =
    store.state.lookupsModule?.lookups?.stipStatuses?.length > 0
      ? store.state.lookupsModule?.lookups?.stipStatuses.find(
          (s: any) => s.id === StipulationStatusIds.PENDING
        )
      : false;
  if (applicationId.value) {
    const response = await updateStipulationStatus(applicationId.value, status, event);
    replaceObjectWithId(sortedItems, response.id, response);
  }
}

onMounted(async () => {
  await getApplicationStips();
  sortedItems = store.getters["checklistModule/sortedItems"];
});
</script>

<style lang="scss" scoped>
.title {
  font-weight: 700;
  font-size: 1.25rem;
}
</style>
